

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from 'Services/APIService';
import { ClockInOutPayload, Shift } from 'types/ndisSection/clockInOutTypes';

interface ShiftState {
  shifts: Shift[];
  loading: boolean;
  error: string | null;
}

const initialState: ShiftState = {
  shifts: [],
  loading: false,
  error: null
};

export const fetchShifts = createAsyncThunk(
  'shifts/fetchShifts',
  async (
    payload: {
      group_id: string;
      service_name_id: string | null;
      component_parent_id: string;
      nested_component_id: string;
      user_whoRequested_id: string;
      loggedIN_user_id: string;
      loggedIN_user_email: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await axiosInstance.post('/scheduled/shifts', payload);
      return response.data as Shift[];
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch shifts');
    }
  }
);

export const clockIn = createAsyncThunk(
  'shifts/clockIn',
  async (
    {
      shift,
      clockInTime,
      payload
    }: {
      shift: Shift;
      clockInTime: string;
      payload: ClockInOutPayload;
    },
    { rejectWithValue }
  ) => {
    try {
      const clockInPayload = {
        ...payload,
        actual_start: clockInTime,
        actual_worked_hours: '0',
        event_id: shift.id,
        shiftId: shift.id,
        clockInTime
      };
      await axiosInstance.post('/scheduledshift/clockin', clockInPayload);
      return {
        shiftId: shift.id,
        clockInTime,
        actual_start: clockInTime,
        actual_worked_hours: '0'
      };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to clock in');
    }
  }
);

export const clockOut = createAsyncThunk(
  'shifts/clockOut',
  async (
    {
      shift,
      clockOutTime,
      clockInTime,
      payload
    }: {
      shift: Shift;
      clockOutTime: string;
      clockInTime: string;
      payload: ClockInOutPayload;
    },
    { rejectWithValue }
  ) => {
    try {
      const start = new Date(clockInTime);
      const end = new Date(clockOutTime);
      const diffInMs = end.getTime() - start.getTime();
      const diffInHours = (diffInMs / (1000 * 60 * 60)).toFixed(2);

      const clockOutPayload = {
        ...payload,
        actual_end: clockOutTime,
        actual_worked_hours: diffInHours,
        event_id: shift.id,
        shiftId: shift.id,
        clockOutTime
      };
      await axiosInstance.post('/scheduledshift/clockout', clockOutPayload);
      return {
        shiftId: shift.id,
        clockOutTime,
        actual_end: clockOutTime,
        actual_worked_hours: diffInHours
      };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to clock out');
    }
  }
);

const shiftSlice = createSlice({
  name: 'clockInOut',
  initialState,
  reducers: {
    resetError(state) {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchShifts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchShifts.fulfilled, (state, action) => {
        state.loading = false;
        state.shifts = action.payload;
      })
      .addCase(fetchShifts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(clockIn.fulfilled, (state, action) => {
        const { shiftId, clockInTime, actual_start, actual_worked_hours } = action.payload;
        const shift = state.shifts.find((s) => s.id === shiftId);
        if (shift) {
          shift.actual_start = actual_start;
          shift.actual_worked_hours = actual_worked_hours;
          shift.clockInTime = clockInTime;
        }
      })
      .addCase(clockIn.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      .addCase(clockOut.fulfilled, (state, action) => {
        const { shiftId, clockOutTime, actual_end, actual_worked_hours } = action.payload;
        const shift = state.shifts.find((s) => s.id === shiftId);
        if (shift) {
          shift.actual_end = actual_end;
          shift.actual_worked_hours = actual_worked_hours;
          shift.clockOutTime = clockOutTime;
        }
      })
      .addCase(clockOut.rejected, (state, action) => {
        state.error = action.payload as string;
      });
  }
});

export const { resetError } = shiftSlice.actions;
export default shiftSlice.reducer;
